import { useCallback, useEffect, useRef, useState } from 'react';

function useScrollTo(id, options, optionalElem, watchInterval = 200) {
  const [hasInitScrolled, setHasInitScrolled] = useState(false);
  const intervalScrollRef = useRef();
  const lastScrollY = useRef(0);
  const totalWatchTimeRef = useRef(0);
  const MIN_WATCH_TIME = 600;
  const MAX_WATCH_TIME = 5000;

  let config = {
    behavior: 'smooth',
    initialScrollDelay: 750,
    offset: 0,
    shouldScrollIntoView: false,
    shouldWatchPosition: false
  };
  const shouldSetInterval = Boolean(id || config.elem);

  if (options && typeof options === 'object') {
    config = {
      ...config,
      ...options
    };
  }
  const configRef = useRef(config);
  const hash = window.location.hash;
  const path = window.location.pathname;
  const scroll = useCallback(
    scrollConfig => {
      try {
        const config = {
          ...configRef.current,
          ...(scrollConfig || {})
        };
        const { behavior, block, offset, shouldScrollIntoView } = config;
        const elem =
          config.elem || (id && document.querySelector(id)) || optionalElem;
        const top = elem
          ? elem.getBoundingClientRect().top + (window.scrollY - offset)
          : null;
        const hasScrolledTop =
          top && !isNaN(top) && window.scrollY >= Math.floor(top);
        const canScrollIntoView = shouldScrollIntoView && elem;

        if (canScrollIntoView) {
          elem.scrollIntoView({ behavior, block });
        } else if (top) {
          window.scrollTo({
            behavior,
            left: 0,
            top
          });
        }
        if (
          ((hasScrolledTop || canScrollIntoView) &&
            intervalScrollRef.current &&
            totalWatchTimeRef.current >= MIN_WATCH_TIME) ||
          totalWatchTimeRef.current >= MAX_WATCH_TIME
        ) {
          clearInterval(intervalScrollRef.current);
        }

        setHasInitScrolled(true);
      } catch (err) {
        if (process.env.__SITE_ENV__ === 'local') {
          console.error(err);
        }
      }
    },
    [id, optionalElem]
  );

  useEffect(() => {
    const config = configRef.current;
    if (id && hash === id) {
      setTimeout(() => {
        scroll();
      }, config.initialScrollDelay);
    }
  }, [
    hash,
    id,
    path /* handle scenarios like store detail to store detail navigation where the hash stays the same but the path changes. */,
    scroll
  ]);

  useEffect(() => {
    if (
      shouldSetInterval &&
      hasInitScrolled &&
      configRef.current.shouldWatchPosition
    ) {
      intervalScrollRef.current = setInterval(() => {
        scroll();
        totalWatchTimeRef.current += watchInterval;
      }, watchInterval);
    }

    return () => {
      if (intervalScrollRef.current) clearInterval(intervalScrollRef.current);
    };
  }, [hasInitScrolled, scroll, shouldSetInterval, watchInterval]);

  // TODO: This is a temporary solution that stops an interval when the user scrolls up.
  // We need to extend this functionality to also properly handle scrolling down.
  const handleScroll = useCallback(() => {
    if (window.scrollY < lastScrollY.current) {
      if (intervalScrollRef.current) {
        clearInterval(intervalScrollRef.current);
        intervalScrollRef.current = null;
      }
    }
    lastScrollY.current = window.scrollY;
  }, []);

  useEffect(() => {
    if (!configRef.current.shouldWatchPosition) return;

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const delayedScroll = useCallback(() => {
    setTimeout(scroll, 50); // allow other scrolls to be triggered first
  }, [scroll]);

  return delayedScroll;
}

export default useScrollTo;
